import { useMemo, useState } from "react";
import { Pressable, StyleSheet, Text } from "react-native";

import type { UiTheme } from "../theme";
import { splitLong } from "../primitives/splitLong";

// #477: a Metria card's description. A long one shows its head and a «…»
// that opens the rest; the whole block is the press target (a nested Text
// takes no hitSlop), 44pt tall. The screen reader hears the full text, as
// in InboxCard; the open state rides in a11y state and aria-expanded.
const DESC_FOLD_THRESHOLD = 120;

export function LibraryItemDescription({ theme, text }: { theme: UiTheme; text: string }) {
  const styles = makeStyles(theme);
  const [open, setOpen] = useState(false);
  const { head, tail } = useMemo(() => splitLong(text, DESC_FOLD_THRESHOLD), [text]);
  if (!tail) return <Text style={styles.desc}>{text}</Text>;
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={text}
      accessibilityState={{ expanded: open }}
      // RNW drops accessibilityState (useDisclosure.ts): this reaches the web.
      aria-expanded={open}
      onPress={() => setOpen((v) => !v)}
      style={({ pressed }) => [styles.fold, pressed && styles.pressed]}
    >
      <Text style={styles.desc}>
        {open ? text : head}
        {open ? null : (
          <>
            {" "}
            <Text style={styles.more}>{"…"}</Text>
          </>
        )}
      </Text>
    </Pressable>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, typography: t } = theme;
  return StyleSheet.create({
    fold: { minHeight: 44 },
    pressed: { opacity: 0.7 },
    desc: { color: p.ink2, fontSize: t.sizes.sm },
    // Not colour alone: heavier and underlined too.
    more: {
      color: p.brandText,
      fontWeight: "700",
      textDecorationLine: "underline",
    },
  });
}
